import { Polygon, Point } from 'ol/geom'
import { getCenter } from 'ol/extent'
import { getRoot } from '/@/root'
import { toLonLat } from 'ol/proj.js'

type LineSegment = {
  start: number[]; // [x, y]
  end: number[]; // [x, y]
};

export type RoutePoint = {
  index: number,
  coordinate: number[],
  lonlat: number[],
  altitude: number,
  point: number[],
  bearing: number,
  turnDamping: number,
  speed: number
}

export type RouteBearing = {
  flightHeightMode: string, // 'aboveGroundLevel', // 'relativeToStartPoint';'aboveGroundLevel'
  globalShootHeight: number,
  surfaceRelativeHeight: number,
  flightDirectionDeg: number,
  boundaryPolygon: Polygon,
  routePointBearings: RoutePoint[],
}

export function UseLawnmower () {
  const root = getRoot()

  // --- DJI M3D Camera Parameters ---
  const camera = {
    sensorWidth: 13.2,
    sensorHeight: 8.8,
    focalLength: 8.8,
    imageWidth: 4000,
    imageHeight: 3000
  }

  // Flight Params
  const gsd = 0.02 // 2 cm/pixel
  const flightParams = { frontOverlap: 0.8, sideOverlap: 0.7 }

  // --- Utilities ---
  // Ground footprint based on GSD
  function calculateFootprint () {
    return {
      width: camera.imageWidth * gsd * (1 - flightParams.sideOverlap),
      height: camera.imageHeight * gsd * (1 - flightParams.frontOverlap)
    }
  }

  // // function bufferPolygon(polygon: Polygon, margin: number): Polygon {
  // //   const reader = new jsts.io.OL3Parser();
  // //   const jstsPolygon = reader.read(polygon); // Convert OL polygon to JSTS geometry
  // //   const buffered = jstsPolygon.buffer(margin); // margin in same units as coordinates
  // //   return reader.write(buffered) as Polygon; // Convert back to OL polygon
  // // }

  // Rotate a point around center by angleDeg clockwise
  function rotatePoint (coord:number[], center:number[], angleDeg:number) {
    const angle = angleDeg * Math.PI / 180
    const cos = Math.cos(angle); const sin = Math.sin(angle)
    const dx = coord[0] - center[0]; const dy = coord[1] - center[1]
    return [center[0] + dx * cos - dy * sin, center[1] + dx * sin + dy * cos]
  }

  // Rotate all points
  function rotateRoutePoints (
    polygon: Polygon,
    points: Point[],
    angleDeg:number
  ):number[][] {
    const extent = polygon.getExtent()
    const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2]
    return points.map(p => rotatePoint(p.getCoordinates(), center, angleDeg))
  }

  // Calculate bearing in degrees from p1 to p2
  function calculateBearing (p1: number[], p2: number[]) {
    const lon1 = p1[0] * Math.PI / 180
    const lat1 = p1[1] * Math.PI / 180
    const lon2 = p2[0] * Math.PI / 180
    const lat2 = p2[1] * Math.PI / 180

    const y = Math.sin(lon2 - lon1) * Math.cos(lat2)
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1)
    const brng = Math.atan2(y, x) * 180 / Math.PI
    return (brng + 360) % 360
  }

  // // Generate lawnmower inside polygon
  // function generateLawnmower (polygon, footprint) {
  //   const extent = polygon.getExtent()
  //   const minX = extent[0]
  //   const minY = extent[1]
  //   const maxX = extent[2]
  //   const maxY = extent[3]
  //   const points = []
  //   let y = minY; let direction = 1
  //   while (y <= maxY) {
  //     if (direction === 1) {
  //       for (let x = minX; x <= maxX; x += footprint.width) {
  //         if (polygon.intersectsCoordinate([x, y])) points.push([x, y])
  //       }
  //     } else {
  //       for (let x = maxX; x >= minX; x -= footprint.width) {
  //         if (polygon.intersectsCoordinate([x, y])) points.push([x, y])
  //       }
  //     }
  //     y += footprint.height
  //     direction *= -1
  //   }
  //   return points
  // }

  // // Generate points along polygon edges
  // // function generateEdgePoints (polygon, spacing) {
  // //   const coords = polygon.getCoordinates()[0]
  // //   const edgePoints = []
  // //   for (let i = 0; i < coords.length - 1; i++) {
  // //     const [x1, y1] = coords[i]; const [x2, y2] = coords[i + 1]
  // //     const dx = x2 - x1; const dy = y2 - y1
  // //     const dist = Math.hypot(dx, dy)
  // //     const segments = Math.max(Math.floor(dist / spacing), 1)
  // //     for (let j = 0; j <= segments; j++) {
  // //       const t = j / segments
  // //       edgePoints.push([x1 + t * dx, y1 + t * dy])
  // //     }
  // //   }
  // //   return edgePoints
  // // }

  // // Combine interior + edge points
  // function generateFullRoute (polygon, footprint) {
  //   // const extent = polygon.getExtent()

  //   // const cx = (extent[0] + extent[2]) / 2
  //   // const cy = (extent[1] + extent[3]) / 2

  //   // Scale polygon by 5%
  //   // polygon.scale(1.1, 1.1, [cx, cy])
  //   // optional anchor (defaults to geometry center if omitted)
  //   const boundary = polygon.clone()
  //   const anchor = boundary.getInteriorPoint().getCoordinates()
  //   // apply scaling
  //   boundary.scale(1.2, 1.2, anchor)

  //   const lawnmower = generateLawnmower(boundary, footprint)
  //   // const edges = generateEdgePoints(polygon, Math.min(footprint.width, footprint.height))
  //   const allPoints = [...lawnmower]
  //   // for (const p of edges) {
  //   //   if (!allPoints.some(pt => Math.hypot(pt[0] - p[0], pt[1] - p[1]) < 1e-6)) allPoints.push(p)
  //   // }
  //   return allPoints
  // }

  function calculateTurnDamping (p1:number[], p2:number[], speed:number = 6):number {
    const xdiff = p2[0] - p1[0]; const ydiff = p2[1] - p1[1]; const zdiff = p2[2] - p1[2]
    const segmentDistance = Math.sqrt(xdiff ** 2 + ydiff ** 2 + zdiff ** 2)

    // === Main logic to choose damping distance ===
    let damping: number

    if (segmentDistance < 30) {
      damping = Math.min(5, segmentDistance * 0.2) // very short segment → keep small
    } else {
      // Base on speed
      if (speed <= 6) damping = 6
      else if (speed <= 10) damping = 10 // your original example value
      else if (speed <= 14) damping = 15
      else damping = 20

      // Make it proportional to segment length (never too aggressive)
      const percentBased = segmentDistance * 0.25 // 25% of segment is usually very safe
      damping = Math.min(damping, percentBased)

      // Safety cap: never exceed 40% of segment on one side
      damping = Math.min(damping, segmentDistance * 0.4)
    }

    // Round to 1 decimal (DJI accepts float)
    damping = Math.round(damping * 10) / 10

    // Check constraint
    const maxSafe = segmentDistance * 0.8 // leave some margin
    if (damping * 2 > maxSafe) {
      damping = Math.floor(maxSafe / 2)
    }

    return damping
  }

  // Generate route with bearing
  function generateRouteWithBearing (
    flightHeightMode: string,
    polygon: Polygon,
    routePoints: Point[],
    bearingDeg: number,
    flightAltitude: number
  ): RouteBearing {
    const rotated = rotateRoutePoints(polygon, routePoints, bearingDeg)
    const routePointBearings:RoutePoint[] = []
    let pPrev: number[] = []
    for (let i = 0; i < rotated.length; i++) {
      const p = rotated[i]
      const pLonLat = toLonLat(p)

      let bearing = 0
      if (i < rotated.length - 1) bearing = calculateBearing(pLonLat, toLonLat(rotated[i + 1]))
      else if (i > 0) bearing = calculateBearing(toLonLat(rotated[i - 1]), pLonLat)

      if (bearing > 180) bearing = bearing - 360

      const layerPixel = root?.$map.getPixelFromCoordinate(p)
      const layerData = root?.$demLayer.getData(layerPixel)

      const altitude = layerData ? layerData[0] + flightAltitude : null
      p[2] = altitude

      const turnDamping = (i > 0) ? calculateTurnDamping(pPrev, p) : 0
      routePointBearings.push(
        { index: i, coordinate: p, lonlat: pLonLat, altitude: altitude, point: p, bearing: bearing, turnDamping: turnDamping, speed: 3 })

      pPrev = p
    }

    return { flightHeightMode: flightHeightMode, flightDirectionDeg: routePointBearings[0].bearing, globalShootHeight: flightAltitude, surfaceRelativeHeight: flightAltitude, boundaryPolygon: polygon, routePointBearings: routePointBearings }
  }

  function rotateCoord (
    coord: number[],
    angleRad: number,
    origin: number[]
  ): number[] {
    const cos = Math.cos(angleRad)
    const sin = Math.sin(angleRad)

    const dx = coord[0] - origin[0]
    const dy = coord[1] - origin[1]

    return [
      origin[0] + dx * cos - dy * sin,
      origin[1] + dx * sin + dy * cos,
    ]
  }

  function rotatePolygon (
    polygon: Polygon,
    angleRad: number
  ): Polygon {
    const extent = polygon.getExtent()
    const origin = getCenter(extent)

    const rotatedCoords = polygon
      .getCoordinates()[0]
      .map(c => rotateCoord(c, angleRad, origin))

    return new Polygon([rotatedCoords])
  }

  function intersectHorizontalLine (
    y: number,
    ring: number[][]
  ): number[] {
    const xs: number[] = []

    for (let i = 0; i < ring.length - 1; i++) {
      const p1 = ring[i]
      const p2 = ring[i + 1]

      if (
        (p1[1] <= y && p2[1] > y) ||
            (p2[1] <= y && p1[1] > y)
      ) {
        const t = (y - p1[1]) / (p2[1] - p1[1])
        xs.push(p1[0] + t * (p2[0] - p1[0]))
      }
    }

    return xs.sort((a, b) => a - b)
  }

  function sampleAlongLine (
    start: number[],
    end: number[],
    step: number
  ): number[][] {
    const points: number[][] = []

    if (calculateDistanceCoord(start, end) > step) {
      const dx = end[0] - start[0]
      const dy = end[1] - start[1]
      const length = Math.hypot(dx, dy)

      const ux = dx / length
      const uy = dy / length

      for (let d = step; d <= length; d += step) {
        points.push([
          start[0] + ux * d,
          start[1] + uy * d
        ])
      }
    }

    return points
  }

  function generateLawnmowerSegments (
    polygon: Polygon,
    lineSpacing: number,
    photoSpacing: number,
    flightPathDirectionDeg: number
  ): LineSegment[] {
    const angleRad = (flightPathDirectionDeg * Math.PI) / 180

    // 1. Rotate polygon so scan lines are horizontal
    const rotated = rotatePolygon(polygon, -angleRad)
    const ring = rotated.getCoordinates()[0]

    const extent = rotated.getExtent()
    const minY = extent[1]
    const maxY = extent[3]

    const origin = getCenter(polygon.getExtent())

    const segments: LineSegment[] = []
    let reverse = false

    // 2. Horizontal sweep
    for (let y = minY; y <= maxY; y += photoSpacing) {
      const xs = intersectHorizontalLine(y, ring)

      for (let i = 0; i < xs.length; i += lineSpacing) {
        if (i + 1 >= xs.length) continue

        let start = [xs[i], y]
        let end = [xs[i + 1], y]

        if (reverse) {
          [start, end] = [end, start]
        }

        // rotate back to world coordinates
        segments.push({
          start: rotateCoord(start, angleRad, origin),
          end: rotateCoord(end, angleRad, origin),
        })
      }

      reverse = !reverse
    }

    return segments
  }

  // function generateLawnmowerPathOLSegments (polygon: Polygon, flightPathDirectionDeg: number):Point[] {
  //   const footprint = calculateFootprint()

  //   const lineSpacing = footprint.width
  //   const photoSpacing = footprint.height

  //   const pathSegments = generateLawnmowerSegments(
  //     polygon,
  //     lineSpacing,
  //     photoSpacing,
  //     flightPathDirectionDeg
  //   )

  //   const waypoints: Point[] = []

  //   for (const seg of pathSegments) {
  //     const samples = sampleAlongLine(
  //       seg.start,
  //       seg.end,
  //       lineSpacing
  //     )

  //     for (const c of samples) {
  //       waypoints.push(new Point(c))
  //     }
  //   }

  //   return waypoints
  // }

  function calculateDistanceCoord (coord1:number[], coord2:number[]) {
    return Math.sqrt(
      (
        (coord1[0] - coord2[0]) * (coord1[0] - coord2[0])
      ) + (
        (coord1[1] - coord2[1]) * (coord1[1] - coord2[1])
      )
    )
  }

  function calculateDistancePoints (p1:Point, p2:Point): number {
    const coord1 = p1.getCoordinates()
    const coord2 = p2.getCoordinates()
    return calculateDistanceCoord(coord1, coord2)
  }

  function generateLawnmowerPath (
    flightHeightMode: string,
    polygon: Polygon,
    flightPathDirectionDeg: number,
    cameraBearingAngleDeg: number,
    flightAltitude: number
  ): RouteBearing {
    const footprint = calculateFootprint()
    const angleRad = (flightPathDirectionDeg * Math.PI) / 180

    // 1. Rotate polygon so scan lines are horizontal
    const rotated = rotatePolygon(polygon, -angleRad)
    const ring = rotated.getCoordinates()[0]

    const extent = rotated.getExtent()
    const minY = extent[1]
    const maxY = extent[3]

    const origin = getCenter(polygon.getExtent())

    const points: Point[] = []

    let reverse = false

    // 2. Horizontal sweep
    for (let y = minY; y <= maxY; y += footprint.height) {
      const xs = intersectHorizontalLine(y, ring)

      for (let i = 0; i < xs.length; i += 2) {
        if (i + 1 >= xs.length) continue

        const a = [xs[i], y]
        const b = [xs[i + 1], y]

        const segment = reverse ? [b, a] : [a, b]

        let rotatedBackPrev:number[] = []

        for (const c of segment) {
          const rotatedBack = rotateCoord(c, angleRad, origin)
          if (rotatedBackPrev.length > 0) {
            const segmentsAlongLine = sampleAlongLine(
              rotatedBackPrev,
              rotatedBack,
              footprint.width
            )
            if (segmentsAlongLine.length > 1) {
              let rotatedBackPushFlag = true
              for (const segmentAlongLine of segmentsAlongLine) {
                const segmentPoint = new Point(segmentAlongLine)
                points.push(segmentPoint)
                if (rotatedBackPushFlag) {
                  rotatedBackPushFlag = calculateDistanceCoord(rotatedBack, segmentAlongLine) <= footprint.width
                }
              }
              if (rotatedBackPushFlag) {
                points.push(new Point(rotatedBack))
              }
            }
          } else {
            points.push(new Point(rotatedBack))
          }
          rotatedBackPrev = rotatedBack
        }
      }

      reverse = !reverse
    }

    const routeBearing = generateRouteWithBearing(
      flightHeightMode, polygon, points, cameraBearingAngleDeg, flightAltitude
    )
    return routeBearing
  }

  return {
    generateLawnmowerPath
  }
}
